import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon, Video, Play } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/PageHero';

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [viewingAlbum, setViewingAlbum] = useState(null); // Album being viewed in modal
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/gallery/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json())
    ])
      .then(([galleryData, settingsData]) => {
        setItems(galleryData);
        setSettings(settingsData);
      })
      .catch(err => console.error('Error fetching data:', err))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  const openAlbumModal = (album) => {
    setViewingAlbum(album);
  };

  const closeAlbumModal = () => {
    setViewingAlbum(null);
  };

  const openLightbox = (album, imageIndex) => {
    setCurrentAlbum(album);
    setCurrentIndex(imageIndex);
    setSelectedImage(album.images[imageIndex]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setCurrentAlbum(null);
  };

  const nextImage = () => {
    if (currentAlbum && currentIndex < currentAlbum.images.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setSelectedImage(currentAlbum.images[newIndex]);
    }
  };

  const prevImage = () => {
    if (currentAlbum && currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setSelectedImage(currentAlbum.images[newIndex]);
    }
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  };

  const photoAlbums = items.filter(item => item.album_type === 'photos' && item.is_published);
  const videoAlbums = items.filter(item => item.album_type === 'videos' && item.is_published);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <PageHero 
        title="معرض الصور والفيديو"
        subtitle="استعرض صور وفيديوهات فعاليات وأنشطة الجامعة"
        backgroundImage={settings?.page_gallery_bg}
      />

      {/* Photo Albums Cards */}
      {photoAlbums.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0F388E] dark:text-white mb-4">معرض الصور</h2>
              <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {photoAlbums.map((album) => (
                <Card 
                  key={album.id} 
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#C9A040]"
                  onClick={() => openAlbumModal(album)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={album.images[0] || 'https://via.placeholder.com/400x300?text=No+Image'}
                      alt={album.title_ar}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="w-5 h-5 text-[#C9A040]" />
                          <span className="text-sm font-semibold">{album.images.length} صورة</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Badge for image count */}
                    <div className="absolute top-4 left-4 bg-[#0F388E] text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                      {album.images.length} صورة
                    </div>
                  </div>
                  
                  <CardContent className="p-6 bg-white dark:bg-gray-800">
                    <h3 className="text-xl font-bold text-[#0F388E] dark:text-white group-hover:text-[#C9A040] transition-colors mb-2">
                      {album.title_ar}
                    </h3>
                    {album.title_en && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{album.title_en}</p>
                    )}
                    {album.description_ar && (
                      <p className="text-gray-600 dark:text-gray-300 mt-3 line-clamp-2">
                        {album.description_ar}
                      </p>
                    )}
                    <div className="mt-4 flex items-center text-[#C9A040] font-semibold text-sm group-hover:gap-2 transition-all">
                      <span>عرض الألبوم</span>
                      <ChevronLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Video Albums Cards */}
      {videoAlbums.length > 0 && (
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0F388E] dark:text-white mb-4">معرض الفيديو</h2>
              <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videoAlbums.map((album) => {
                const firstVideoId = getYouTubeVideoId(album.images[0]);
                const thumbnailUrl = firstVideoId 
                  ? `https://img.youtube.com/vi/${firstVideoId}/maxresdefault.jpg`
                  : 'https://via.placeholder.com/400x300?text=Video';
                
                return (
                  <Card 
                    key={album.id} 
                    className="group overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#C9A040]"
                    onClick={() => openAlbumModal(album)}
                  >
                    <div className="relative h-64 overflow-hidden bg-black">
                      <img
                        src={thumbnailUrl}
                        alt={album.title_ar}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-80"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300?text=Video';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all flex items-center justify-center">
                        <div className="w-20 h-20 bg-[#C9A040] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                          <Play className="w-10 h-10 text-white mr-1" fill="currentColor" />
                        </div>
                      </div>
                      
                      {/* Badge for video count */}
                      <div className="absolute top-4 left-4 bg-[#C9A040] text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg flex items-center gap-1">
                        <Video className="w-4 h-4" />
                        {album.images.length} فيديو
                      </div>
                    </div>
                    
                    <CardContent className="p-6 bg-white dark:bg-gray-800">
                      <h3 className="text-xl font-bold text-[#0F388E] dark:text-white group-hover:text-[#C9A040] transition-colors mb-2">
                        {album.title_ar}
                      </h3>
                      {album.title_en && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{album.title_en}</p>
                      )}
                      {album.description_ar && (
                        <p className="text-gray-600 dark:text-gray-300 mt-3 line-clamp-2">
                          {album.description_ar}
                        </p>
                      )}
                      <div className="mt-4 flex items-center text-[#C9A040] font-semibold text-sm group-hover:gap-2 transition-all">
                        <span>عرض الفيديوهات</span>
                        <ChevronLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {items.length === 0 && (
        <section className="py-20">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 text-lg">لا توجد عناصر في المعرض حالياً</p>
          </div>
        </section>
      )}

      {/* Album Modal - Shows all images/videos in album */}
      {viewingAlbum && (
        <div 
          className="fixed inset-0 z-40 bg-black/90 flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeAlbumModal}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-6 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold text-[#0F388E] dark:text-white">
                  {viewingAlbum.title_ar}
                </h2>
                {viewingAlbum.title_en && (
                  <p className="text-gray-500 dark:text-gray-400 mt-1">{viewingAlbum.title_en}</p>
                )}
              </div>
              <button
                onClick={closeAlbumModal}
                className="text-gray-500 hover:text-[#0F388E] dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {viewingAlbum.description_ar && (
                <p className="text-gray-600 dark:text-gray-300 mb-6">{viewingAlbum.description_ar}</p>
              )}

              {/* Photos Grid */}
              {viewingAlbum.album_type === 'photos' && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {viewingAlbum.images.map((image, index) => (
                    <div
                      key={index}
                      className="relative h-48 rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => {
                        closeAlbumModal();
                        openLightbox(viewingAlbum, index);
                      }}
                    >
                      <img
                        src={image}
                        alt={`${viewingAlbum.title_ar} ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x200?text=Error';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                        <ImageIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Videos Grid */}
              {viewingAlbum.album_type === 'videos' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {viewingAlbum.images.map((videoUrl, index) => {
                    const videoId = getYouTubeVideoId(videoUrl);
                    return (
                      <div key={index} className="relative rounded-lg overflow-hidden shadow-lg">
                        {videoId ? (
                          <iframe
                            className="w-full h-64"
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={`${viewingAlbum.title_ar} - فيديو ${index + 1}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        ) : (
                          <div className="w-full h-64 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <a 
                              href={videoUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              فتح الفيديو
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={closeLightbox}>
          <button
            className="absolute top-4 left-4 text-white hover:text-[#C9A040] transition-colors"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>

          {currentAlbum && currentIndex > 0 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#C9A040] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
            >
              <ChevronRight className="w-12 h-12" />
            </button>
          )}

          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage}
              alt="Selected"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
            {currentAlbum && (
              <div className="text-center mt-4 text-white">
                <p className="text-lg">
                  {currentIndex + 1} / {currentAlbum.images.length}
                </p>
              </div>
            )}
          </div>

          {currentAlbum && currentIndex < currentAlbum.images.length - 1 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#C9A040] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
            >
              <ChevronLeft className="w-12 h-12" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Gallery;
